/**
 * Notifications API - In-app notifications
 */
import apiClient from './index'

export interface Notification {
  id: string
  notification_type: 'info' | 'success' | 'warning' | 'task' | 'deal' | 'contact' | 'system'
  title: string
  message: string
  link?: string
  entity_type?: string
  entity_id?: string
  is_read: boolean
  read_at?: string
  created_at: string
}

export interface NotificationsResponse {
  data: Notification[]
}

export interface NotificationResponse {
  data: Notification
}

export interface UnreadCountResponse {
  data: { unread: number }
}

/**
 * Get notifications for current user
 */
export async function getNotifications(options?: {
  includeRead?: boolean
  limit?: number
}): Promise<Notification[]> {
  const params = new URLSearchParams()

  if (options?.includeRead) {
    params.set('include_read', 'true')
  }
  if (options?.limit) {
    params.set('limit', options.limit.toString())
  }

  const url = params.toString() ? `/notifications?${params.toString()}` : '/notifications'
  const response = await apiClient.get<NotificationsResponse>(url)
  return response.data.data
}

/**
 * Get count of unread notifications
 */
export async function getUnreadCount(): Promise<number> {
  const response = await apiClient.get<UnreadCountResponse>('/notifications/count')
  return response.data.data.unread
}

/**
 * Mark a notification as read
 */
export async function markAsRead(notificationId: string): Promise<Notification> {
  const response = await apiClient.post<NotificationResponse>(
    `/notifications/${notificationId}/read`
  )
  return response.data.data
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<number> {
  const response = await apiClient.post<{ data: { marked_read: number } }>(
    '/notifications/read-all'
  )
  return response.data.data.marked_read
}

/**
 * Delete a notification
 */
export async function deleteNotification(notificationId: string): Promise<void> {
  await apiClient.delete(`/notifications/${notificationId}`)
}
